import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface LacquerTechnique {
  name: string;
  process: string;
  effect: string;
  color: string;
}

interface LacquerOrigin {
  name: string;
  specialty: string;
  color: string;
}

interface LacquerProperty {
  label: string;
  value: number;
  color: string;
}

interface TimelineNode {
  era: string;
  event: string;
}

interface ColorSwatch {
  name: string;
  hex: string;
}

interface LacquerwareTemplateProps {
  topic: string;
  subtitle: string;
  techniques: LacquerTechnique[];
  processSteps: string[];
  origins: LacquerOrigin[];
  properties: LacquerProperty[];
  timeline: TimelineNode[];
  colorPalette: ColorSwatch[];
  stats: { value: string; label: string }[];
  bottomWisdom: string;
}

const DEFAULT_PROPS: LacquerwareTemplateProps = {
  topic: '漆器艺术与大漆工艺',
  subtitle: 'Lacquerware Art & Natural Lacquer Craft',
  techniques: [
    {
      name: '雕漆',
      process: '在厚漆层上逐层雕刻纹饰',
      effect: '立体浮雕感，层次丰富，气势恢宏',
      color: '#b91c1c',
    },
    {
      name: '描金',
      process: '以金粉或金箔在漆面描绘纹样',
      effect: '金碧辉煌，线条细腻，富丽堂皇',
      color: '#fbbf24',
    },
    {
      name: '螺钿镶嵌',
      process: '将贝壳薄片裁切镶入漆面',
      effect: '彩虹光泽，流转变幻，精美绝伦',
      color: '#06b6d4',
    },
    {
      name: '犀皮',
      process: '多色漆层交替涂抹后打磨',
      effect: '天然纹理如行云流水，独一无二',
      color: '#a855f7',
    },
  ],
  processSteps: ['制胎', '涂漆', '阴干', '打磨', '装饰', '推光'],
  origins: [
    { name: '福州脱胎', specialty: '脱胎技法，轻巧坚固，薄如蝉翼', color: '#b91c1c' },
    { name: '扬州雕漆', specialty: '红雕漆工艺，刀法精湛，层次分明', color: '#fbbf24' },
    { name: '平遥推光', specialty: '手掌推光，漆面如镜，光可鉴人', color: '#06b6d4' },
    { name: '成都漆器', specialty: '雕花填彩，色彩斑斓，工艺独特', color: '#22c55e' },
  ],
  properties: [
    { label: '耐酸', value: 95, color: '#b91c1c' },
    { label: '耐碱', value: 90, color: '#fbbf24' },
    { label: '耐热', value: 85, color: '#06b6d4' },
    { label: '防水', value: 98, color: '#22c55e' },
  ],
  timeline: [
    { era: '河姆渡', event: '7000年前最早漆器' },
    { era: '战国', event: '彩绘漆器鼎盛' },
    { era: '唐代', event: '描金技法成熟' },
    { era: '明清', event: '雕漆登峰造极' },
  ],
  colorPalette: [
    { name: '朱红', hex: '#b91c1c' },
    { name: '漆黑', hex: '#1c1917' },
    { name: '描金', hex: '#fbbf24' },
    { name: '螺钿绿', hex: '#059669' },
  ],
  stats: [
    { value: '7000+年', label: '漆器历史' },
    { value: '3-5天', label: '每层干燥时间' },
    { value: '数月', label: '一件精品耗时' },
  ],
  bottomWisdom: '百遍涂之，千遍磨之——漆器是时间与匠心凝结的艺术',
};

export const LacquerwareTemplate: React.FC<Partial<LacquerwareTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const bowlDraw = interpolate(frame, [15, 90], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const techStart = 80;
  const processStart = 200;
  const originStart = 320;
  const propStart = 440;
  const timelineStart = 520;
  const swatchStart = 600;
  const layerCountStart = 660;
  const statsStart = 720;
  const wisdomStart = 780;

  const wisdomOpacity = interpolate(frame, [wisdomStart, wisdomStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const layerCount = Math.round(
    interpolate(frame, [layerCountStart, layerCountStart + 90], [0, 100], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    })
  );

  const bowlLayers = [
    { label: '木胎', color: '#78350f', y: 202, h: 38 },
    { label: '灰底', color: '#57534e', y: 164, h: 38 },
    { label: '漆层', color: '#1c1917', y: 126, h: 38 },
    { label: '装饰层', color: '#b91c1c', y: 88, h: 38 },
  ];

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(170deg, #1a0a06 0%, #0f0604 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Lacquer sheen spots */}
      {Array.from({ length: 40 }).map((_, i) => {
        const x = (i * 191) % 1920;
        const y = (i * 283) % 1080;
        const size = 40 + (i % 5) * 20;
        return (
          <div
            key={`sheen-${i}`}
            style={{
              position: 'absolute',
              left: x - size / 2,
              top: y - size / 2,
              width: size,
              height: size,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(185,28,28,0.06) 0%, transparent 70%)',
            }}
          />
        );
      })}

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 24,
          left: 40,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 16}px)`,
        }}
      >
        <div
          style={{
            padding: '5px 16px',
            background: '#451a03',
            borderRadius: 8,
            fontSize: 22,
            fontWeight: 700,
            color: '#fbbf24',
            border: '1px solid #78350f',
            whiteSpace: 'nowrap',
          }}
        >
          🏺 大漆工艺
        </div>
        <h1 style={{ fontSize: 38, fontWeight: 900, margin: 0, color: '#fef2f2', whiteSpace: 'nowrap' }}>
          {p.topic}
        </h1>
        <p style={{ fontSize: 26, color: '#a8a29e', margin: 0, whiteSpace: 'nowrap' }}>{p.subtitle}</p>
      </div>

      {/* Key badge */}
      <div
        style={{
          position: 'absolute',
          top: 24,
          right: 40,
          textAlign: 'right',
          opacity: headerIn,
        }}
      >
        <div style={{ fontSize: 24, color: '#a8a29e' }}>传承工艺</div>
        <div style={{ fontSize: 28, fontWeight: 900, color: '#b91c1c' }}>百遍髹漆</div>
      </div>

      {/* ===== LEFT PANEL: Techniques ===== */}
      <div style={{ position: 'absolute', top: 100, left: 40, width: 400 }}>
        <div style={{ fontSize: 26, color: '#a8a29e', marginBottom: 10, letterSpacing: '0.08em', fontWeight: 600 }}>
          四大技法
        </div>
        {p.techniques.map((t, i) => {
          const s = spring({
            frame: frame - (techStart + i * 18),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#1a0e08',
                borderRadius: 12,
                padding: '12px 16px',
                marginBottom: 10,
                border: `1px solid ${t.color}30`,
                opacity: s,
                transform: `translateX(${(1 - s) * -30}px)`,
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 800, color: t.color, marginBottom: 3 }}>
                {t.name}
              </div>
              <div style={{ fontSize: 22, color: '#d6d3d1', lineHeight: 1.4, marginBottom: 2 }}>
                {t.process}
              </div>
              <div style={{ fontSize: 22, color: '#a8a29e', lineHeight: 1.3 }}>{t.effect}</div>
            </div>
          );
        })}
      </div>

      {/* ===== Layer Counter — below techniques ===== */}
      {/* <div
        style={{
          position: 'absolute',
          top: 580,
          left: 40,
          width: 400,
          textAlign: 'center',
          opacity: interpolate(frame, [layerCountStart, layerCountStart + 20], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
        }}
      >
        <div style={{ fontSize: 14, color: '#a8a29e', marginBottom: 4 }}>涂漆层数</div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4 }}>
          <div style={{ fontSize: 56, fontWeight: 900, color: '#b91c1c', lineHeight: 1 }}>
            {layerCount}
          </div>
          <div style={{ fontSize: 22, color: '#78716c' }}>/ 100 层</div>
        </div>
      </div> */}

      {/* ===== CENTER: SVG Lacquer Bowl with Cross-Section ===== */}
      <svg
        style={{ position: 'absolute', left: 430, top: 55 }}
        width="890"
        height="420"
        viewBox="0 0 760 320"
      >
        <defs>
          <radialGradient id="lacquerSheen" cx="40%" cy="30%">
            <stop offset="0%" stopColor="#b91c1c" stopOpacity="0.4" />
            <stop offset="60%" stopColor="#1c1917" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#0f0604" stopOpacity="1" />
          </radialGradient>
        </defs>

        <path
          d="M120,240 Q120,120 250,100 Q350,85 450,100 Q580,120 580,240 Z"
          fill="url(#lacquerSheen)"
          stroke="#b91c1c"
          strokeWidth="2"
          opacity={bowlDraw}
        />
        <ellipse
          cx="350"
          cy="240"
          rx="230"
          ry="30"
          fill="#1c1917"
          stroke="#78350f"
          strokeWidth="1.5"
          opacity={bowlDraw}
        />
        <path
          d="M150,240 Q150,160 260,140 Q350,128 440,140 Q550,160 550,240"
          fill="none"
          stroke="#b91c1c"
          strokeWidth="1.5"
          strokeDasharray="4 3"
          opacity={bowlDraw * 0.5}
        />

        <text x="680" y="78" fill="#a8a29e" fontSize="22" fontWeight="700" textAnchor="middle" opacity={bowlDraw}>
          截面
        </text>
        {bowlLayers.map((layer, i) => {
          const layerProgress = interpolate(frame, [40 + i * 18, 60 + i * 18], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <g key={`layer-${i}`} opacity={layerProgress}>
              <rect
                x="660"
                y={layer.y}
                width="80"
                height={layer.h}
                rx="4"
                fill={layer.color}
                stroke="#fbbf2430"
                strokeWidth="1"
              />
              <text
                x="650"
                y={layer.y + 25}
                fill="#d6d3d1"
                fontSize="20"
                textAnchor="end"
                fontWeight="700"
              >
                {layer.label}
              </text>
            </g>
          );
        })}

        <path
          d="M200,200 Q280,170 350,175 Q420,170 500,200"
          fill="none"
          stroke="#fbbf24"
          strokeWidth="1.5"
          strokeDasharray={`${bowlDraw * 350}`}
          strokeDashoffset={350 * (1 - bowlDraw)}
          opacity={0.6}
        />
      </svg>

      {/* ===== Process Steps — below bowl ===== */}
      <div style={{ position: 'absolute', top: 430, left: 580, width: 550 }}>
        <div style={{ fontSize: 26, color: '#a8a29e', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          制作工序 <span style={{ fontSize: 2, color: '#78716c' }}>（涂漆重复30+层）</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
          {p.processSteps.map((step, i) => {
            const s = spring({
              frame: frame - (processStart + i * 16),
              fps,
              config: { damping: 12, stiffness: 140 },
            });
            const isRepeat = step === '涂漆';
            const isLast = i === p.processSteps.length - 1;
            return (
              <React.Fragment key={i}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    opacity: s,
                    transform: `scale(${s})`,
                  }}
                >
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      background: isRepeat ? '#451a03' : '#1a0e08',
                      border: `2px solid ${isRepeat ? '#b91c1c' : '#78350f'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 21,
                      fontWeight: 800,
                      color: isRepeat ? '#fbbf24' : '#d6d3d1',
                    }}
                  >
                    {step}
                  </div>
                  {isRepeat && (
                    <div style={{ fontSize: 20, color: '#b91c1c', marginTop: 2, fontWeight: 700 }}>
                      ×30+层
                    </div>
                  )}
                </div>
                {!isLast && (
                  <div
                    style={{
                      width: 32,
                      height: 2,
                      background: 'linear-gradient(90deg, #b91c1c, #b91c1c40)',
                      margin: '0 3px',
                      marginBottom: isRepeat ? 14 : 0,
                      opacity: interpolate(
                        frame,
                        [processStart + i * 16 + 8, processStart + i * 16 + 20],
                        [0, 0.7],
                        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
                      ),
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ===== Timeline — below process ===== */}
      <div style={{ position: 'absolute', top: 600, left: 580, width: 550 }}>
        <div style={{ fontSize: 26, color: '#a8a29e', marginBottom: 14, letterSpacing: '0.08em', fontWeight: 600 }}>
          历史脉络
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          {p.timeline.map((t, i) => {
            const s = spring({
              frame: frame - (timelineStart + i * 18),
              fps,
              config: { damping: 12, stiffness: 140 },
            });
            const isLast = i === p.timeline.length - 1;
            return (
              <React.Fragment key={i}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flex: 1,
                    opacity: s,
                    transform: `translateY(${(1 - s) * 12}px)`,
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      background: '#b91c1c',
                      border: '2px solid #fbbf24',
                      marginBottom: 6,
                    }}
                  />
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#fbbf24' }}>{t.era}</div>
                  <div style={{ fontSize: 18, color: '#d6d3d1', textAlign: 'center', marginTop: 3, lineHeight: 1.3 }}>
                    {t.event}
                  </div>
                </div>
                {!isLast && (
                  <div
                    style={{
                      width: 36,
                      height: 2,
                      background: 'linear-gradient(90deg, #b91c1c, #b91c1c40)',
                      marginTop: 9,
                      opacity: interpolate(
                        frame,
                        [timelineStart + i * 18 + 10, timelineStart + i * 18 + 22],
                        [0, 0.7],
                        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
                      ),
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ===== Color Palette — below timeline ===== */}
      <div style={{ position: 'absolute', top: 780, left: 580, width: 550 }}>
        <div style={{ fontSize: 24, color: '#a8a29e', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          传统漆色
        </div>
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center' }}>
          {p.colorPalette.map((c, i) => {
            const s = spring({
              frame: frame - (swatchStart + i * 12),
              fps,
              config: { damping: 14, stiffness: 130 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  opacity: s,
                  transform: `scale(${s})`,
                }}
              >
                <div
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: 12,
                    background: c.hex,
                    border: '2px solid #fbbf2440',
                    marginBottom: 6,
                    boxShadow: `0 0 16px ${c.hex}30`,
                  }}
                />
                <div style={{ fontSize: 18, fontWeight: 700, color: '#d6d3d1' }}>{c.name}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== RIGHT PANEL: Famous Origins ===== */}
      <div style={{ position: 'absolute', top: 100, right: 50, width: 450 }}>
        <div style={{ fontSize: 26, color: '#a8a29e', marginBottom: 10, letterSpacing: '0.08em', fontWeight: 600 }}>
          四大名漆
        </div>
        {p.origins.map((o, i) => {
          const s = spring({
            frame: frame - (originStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#1a0e08',
                borderRadius: 10,
                padding: '10px 14px',
                marginBottom: 8,
                borderLeft: `4px solid ${o.color}`,
                opacity: s,
                transform: `translateX(${(1 - s) * -30}px)`,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 800, color: o.color, whiteSpace: 'nowrap' }}>
                {o.name}
              </div>
              <div style={{ fontSize: 22, color: '#d6d3d1', lineHeight: 1.3 }}>{o.specialty}</div>
            </div>
          );
        })}
      </div>

      {/* ===== RIGHT: Material Properties ===== */}
      <div style={{ position: 'absolute', top: 500, right: 50, width: 450 }}>
        <div style={{ fontSize: 26, color: '#a8a29e', marginBottom: 10, letterSpacing: '0.08em', fontWeight: 600 }}>
          大漆特性 <span style={{ fontSize: 24, color: '#78716c' }}>（漆酚 Urushiol）</span>
        </div>
        {p.properties.map((pr, i) => {
          const barWidth = interpolate(
            frame,
            [propStart + i * 14, propStart + i * 14 + 50],
            [0, pr.value],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );
          const s = spring({
            frame: frame - (propStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                marginBottom: 10,
                opacity: s,
                transform: `translateX(${(1 - s) * -20}px)`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: 24, fontWeight: 700, color: pr.color }}>{pr.label}</span>
                <span style={{ fontSize: 24, color: '#a8a29e' }}>{Math.round(barWidth)}%</span>
              </div>
              <div
                style={{
                  height: 16,
                  borderRadius: 4,
                  background: '#1c191720',
                  border: '1px solid #78350f30',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${barWidth}%`,
                    height: '100%',
                    borderRadius: 4,
                    background: `linear-gradient(90deg, ${pr.color}, ${pr.color}90)`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Stats ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 70,
          left: 40,
          right: 40,
          display: 'flex',
          gap: 14,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#1a0e08',
                borderRadius: 10,
                padding: '10px 8px',
                textAlign: 'center',
                border: '1px solid #78350f30',
                opacity: stIn,
                transform: `scale(${0.85 + 0.15 * stIn})`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4 }}>
                <span style={{ fontSize: 28, fontWeight: 900, color: '#b91c1c' }}>{st.value}</span>
                <span style={{ fontSize: 22, color: '#a8a29e' }}>{st.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Wisdom ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 5,
          left: 40,
          right: 40,
          background: '#1c0a06',
          borderRadius: 12,
          padding: '12px 20px',
          borderTop: '3px solid #b91c1c',
          opacity: wisdomOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🏺</span>
          <span style={{ fontSize: 20, color: '#fef2f2', lineHeight: 1.5 }}>{p.bottomWisdom}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 900], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #78350f, #b91c1c, #fbbf24, #06b6d4)',
        }}
      />
    </AbsoluteFill>
  );
};
